import React, { useState } from 'react';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ChevronDown, ChevronRight } from 'lucide-react';

interface TagBadgeStyleEditorProps {
  // Enhanced badge colors (for all tags when using Enhanced Badge Styling)
  enhancedPrimaryColor?: string;
  enhancedSecondaryColor?: string;
  onEnhancedPrimaryColorChange?: (color: string) => void;
  onEnhancedSecondaryColorChange?: (color: string) => void;
  showEnhancedSection?: boolean;

  // Special badge colors (for dietary tags when using Special Badge Styling)
  showSpecialSection?: boolean;

  // Vegan colors
  veganPrimaryColor: string;
  veganSecondaryColor: string;
  onVeganPrimaryColorChange: (color: string) => void;
  onVeganSecondaryColorChange: (color: string) => void;

  // Vegetarian colors
  vegetarianPrimaryColor: string;
  vegetarianSecondaryColor: string;
  onVegetarianPrimaryColorChange: (color: string) => void;
  onVegetarianSecondaryColorChange: (color: string) => void;

  // Gluten-free colors
  glutenFreePrimaryColor: string;
  glutenFreeSecondaryColor: string;
  onGlutenFreePrimaryColorChange: (color: string) => void;
  onGlutenFreeSecondaryColorChange: (color: string) => void;

  // Spicy colors
  spicyPrimaryColor: string;
  spicySecondaryColor: string;
  onSpicyPrimaryColorChange: (color: string) => void;
  onSpicySecondaryColorChange: (color: string) => void;

  // Dairy-free colors
  dairyFreePrimaryColor: string;
  dairyFreeSecondaryColor: string;
  onDairyFreePrimaryColorChange: (color: string) => void;
  onDairyFreeSecondaryColorChange: (color: string) => void;

  // Nut-free colors
  nutFreePrimaryColor: string;
  nutFreeSecondaryColor: string;
  onNutFreePrimaryColorChange: (color: string) => void;
  onNutFreeSecondaryColorChange: (color: string) => void;

  // Halal colors
  halalPrimaryColor: string;
  halalSecondaryColor: string;
  onHalalPrimaryColorChange: (color: string) => void;
  onHalalSecondaryColorChange: (color: string) => void;

  // Popular colors
  popularPrimaryColor: string;
  popularSecondaryColor: string;
  onPopularPrimaryColorChange: (color: string) => void;
  onPopularSecondaryColorChange: (color: string) => void;

  // New colors
  newPrimaryColor: string;
  newSecondaryColor: string;
  onNewPrimaryColorChange: (color: string) => void;
  onNewSecondaryColorChange: (color: string) => void;

  // Deal colors
  dealPrimaryColor: string;
  dealSecondaryColor: string;
  onDealPrimaryColorChange: (color: string) => void;
  onDealSecondaryColorChange: (color: string) => void;

  // Brand colors for reference
  primaryColor: string;
  secondaryColor: string;
}

/**
 * TagBadgeStyleEditor - Color customization for dietary tag badges
 */
const TagBadgeStyleEditor: React.FC<TagBadgeStyleEditorProps> = ({
  enhancedPrimaryColor,
  enhancedSecondaryColor,
  onEnhancedPrimaryColorChange,
  onEnhancedSecondaryColorChange,
  showEnhancedSection = false,
  showSpecialSection = false,
  veganPrimaryColor,
  veganSecondaryColor,
  onVeganPrimaryColorChange,
  onVeganSecondaryColorChange,
  vegetarianPrimaryColor,
  vegetarianSecondaryColor,
  onVegetarianPrimaryColorChange,
  onVegetarianSecondaryColorChange,
  glutenFreePrimaryColor,
  glutenFreeSecondaryColor,
  onGlutenFreePrimaryColorChange,
  onGlutenFreeSecondaryColorChange,
  spicyPrimaryColor,
  spicySecondaryColor,
  onSpicyPrimaryColorChange,
  onSpicySecondaryColorChange,
  dairyFreePrimaryColor,
  dairyFreeSecondaryColor,
  onDairyFreePrimaryColorChange,
  onDairyFreeSecondaryColorChange,
  nutFreePrimaryColor,
  nutFreeSecondaryColor,
  onNutFreePrimaryColorChange,
  onNutFreeSecondaryColorChange,
  halalPrimaryColor,
  halalSecondaryColor,
  onHalalPrimaryColorChange,
  onHalalSecondaryColorChange,
  popularPrimaryColor,
  popularSecondaryColor,
  onPopularPrimaryColorChange,
  onPopularSecondaryColorChange,
  newPrimaryColor,
  newSecondaryColor,
  onNewPrimaryColorChange,
  onNewSecondaryColorChange,
  dealPrimaryColor,
  dealSecondaryColor,
  onDealPrimaryColorChange,
  onDealSecondaryColorChange,
  primaryColor,
  secondaryColor
}) => {
  const initialSections = showEnhancedSection ? ['enhanced'] : showSpecialSection ? ['vegan'] : [];
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(initialSections));

  const toggleSection = (section: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(section)) {
      newExpanded.delete(section);
    } else {
      newExpanded.add(section);
    }
    setExpandedSections(newExpanded);
  };

  const colorPresets = [
    { name: 'Primary', color: primaryColor },
    { name: 'Secondary', color: secondaryColor },
    { name: 'White', color: '#ffffff' },
    { name: 'Black', color: '#2D2D2D' },
    { name: 'Green', color: '#32CD32' },
    { name: 'Light Green', color: '#90EE90' },
    { name: 'Orange', color: '#FF8C00' },
    { name: 'Light Orange', color: '#FFB347' },
    { name: 'Red', color: '#FF3333' },
    { name: 'Light Red', color: '#FF6B6B' },
    { name: 'Blue', color: '#4682B4' },
    { name: 'Light Blue', color: '#87CEEB' },
    { name: 'Brown', color: '#8B7355' },
    { name: 'Light Brown', color: '#DEB887' }
  ];

  const applyBrandColorsToAll = () => {
    // Apply brand colors to all tag types
    onVeganPrimaryColorChange(primaryColor);
    onVeganSecondaryColorChange(secondaryColor);
    onVegetarianPrimaryColorChange(primaryColor);
    onVegetarianSecondaryColorChange(secondaryColor);
    onGlutenFreePrimaryColorChange(primaryColor);
    onGlutenFreeSecondaryColorChange(secondaryColor);
    onSpicyPrimaryColorChange(primaryColor);
    onSpicySecondaryColorChange(secondaryColor);
    onDairyFreePrimaryColorChange(primaryColor);
    onDairyFreeSecondaryColorChange(secondaryColor);
    onNutFreePrimaryColorChange(primaryColor);
    onNutFreeSecondaryColorChange(secondaryColor);
    onHalalPrimaryColorChange(primaryColor);
    onHalalSecondaryColorChange(secondaryColor);
    onPopularPrimaryColorChange(primaryColor);
    onPopularSecondaryColorChange(secondaryColor);
    onNewPrimaryColorChange(primaryColor);
    onNewSecondaryColorChange(secondaryColor);
    onDealPrimaryColorChange(primaryColor);
    onDealSecondaryColorChange(secondaryColor);
  };

  const resetAllToDefaults = () => {
    // Reset to default colors
    onVeganPrimaryColorChange('#26c526');
    onVeganSecondaryColorChange('#166a16');
    onVegetarianPrimaryColorChange('#32CD32');
    onVegetarianSecondaryColorChange('#36AB36');
    onGlutenFreePrimaryColorChange('#FFB347');
    onGlutenFreeSecondaryColorChange('#FF8C00');
    onSpicyPrimaryColorChange('#FF6B6B');
    onSpicySecondaryColorChange('#FF3333');
    onDairyFreePrimaryColorChange('#87CEEB');
    onDairyFreeSecondaryColorChange('#4682B4');
    onNutFreePrimaryColorChange('#DEB887');
    onNutFreeSecondaryColorChange('#8B7355');
    onHalalPrimaryColorChange('#019000');
    onHalalSecondaryColorChange('#B8860B');
    onPopularPrimaryColorChange('#b400fa');
    onPopularSecondaryColorChange('#ff0000');
    onNewPrimaryColorChange('#ff0000');
    onNewSecondaryColorChange('#3f92ff');
    onDealPrimaryColorChange('#4fc060');
    onDealSecondaryColorChange('#ff0000');
  };

  const renderColorPicker = (
    value: string,
    onChange: (color: string) => void,
    label: string
  ) => (
    <div className="space-y-2">
      <Label className="text-xs font-medium">{label}</Label>
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className="w-full justify-start gap-2 h-8">
            <div
              className="w-3 h-3 rounded border border-gray-300"
              style={{ backgroundColor: value }}
            />
            <span className="text-xs font-mono">{value}</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-64">
          <div className="space-y-3">
            <Input
              type="color"
              value={value}
              onChange={(e) => onChange(e.target.value)}
              className="w-full h-10"
            />
            <Input
              type="text"
              value={value}
              onChange={(e) => onChange(e.target.value)}
              placeholder="#ffffff"
              className="font-mono text-sm"
            />
            <div className="grid grid-cols-3 gap-1">
              {colorPresets.map((preset) => (
                <Button
                  key={preset.name}
                  variant="outline"
                  size="sm"
                  onClick={() => onChange(preset.color)}
                  className="h-auto p-1 text-xs"
                >
                  <div
                    className="w-3 h-3 rounded border border-gray-300 mr-1"
                    style={{ backgroundColor: preset.color }}
                  />
                  <span className="text-[10px]">{preset.name}</span>
                </Button>
              ))}
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );

  const renderTagSection = (
    title: string,
    sectionKey: string,
    primaryColor: string,
    secondaryColor: string,
    onPrimaryChange: (color: string) => void,
    onSecondaryChange: (color: string) => void,
    previewTags: string[],
    resetColors: { primary: string; secondary: string }
  ) => {
    const isExpanded = expandedSections.has(sectionKey);

    return (
      <div className="border rounded-lg p-3">
        <button
          onClick={() => toggleSection(sectionKey)}
          className="flex items-center justify-between w-full text-left"
        >
          <h4 className="text-sm font-medium">{title}</h4>
          {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </button>

        {isExpanded && (
          <div className="mt-3 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              {renderColorPicker(primaryColor, onPrimaryChange, 'Primary')}
              {renderColorPicker(secondaryColor, onSecondaryChange, 'Secondary')}
            </div>

            <div className="flex items-center justify-between">
              <div className="flex gap-2">
                {previewTags.map(tag => (
                  <span
                    key={tag}
                    className="text-xs font-semibold px-2 py-1 rounded-full text-white"
                    style={{
                      background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`,
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}
                  >
                    {tag}
                  </span>
                ))}
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  onPrimaryChange(resetColors.primary);
                  onSecondaryChange(resetColors.secondary);
                }}
                className="text-xs h-7"
              >
                Reset
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <Card>
      <CardHeader className="pb-4">
        <CardTitle className="text-base">Tag Badge Color Customization</CardTitle>
        {showSpecialSection && (
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={applyBrandColorsToAll}
              className="text-xs"
            >
              Apply Brand Colors to All
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={resetAllToDefaults}
              className="text-xs"
            >
              Reset All
            </Button>
          </div>
        )}
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {/* Enhanced Badge Section - appears when Enhanced Badge Styling is selected */}
          {showEnhancedSection && enhancedPrimaryColor && enhancedSecondaryColor && (
            renderTagSection(
              'Enhanced Badge Colors (All Tags)',
              'enhanced',
              enhancedPrimaryColor,
              enhancedSecondaryColor,
              onEnhancedPrimaryColorChange || (() => {}),
              onEnhancedSecondaryColorChange || (() => {}),
              ['All Tags', 'Vegan', 'Gluten Free', 'Spicy'],
              { primary: primaryColor, secondary: secondaryColor }
            )
          )}

          {/* Special Badge Sections - appear when Special Badge Styling is selected */}
          {showSpecialSection && (
            <>
          {renderTagSection(
            'Vegan Tags',
            'vegan',
            veganPrimaryColor,
            veganSecondaryColor,
            onVeganPrimaryColorChange,
            onVeganSecondaryColorChange,
            ['Vegan', 'Plant-Based'],
            { primary: '#26c526', secondary: '#166a16' }
          )}

          {renderTagSection(
            'Vegetarian Tags',
            'vegetarian',
            vegetarianPrimaryColor,
            vegetarianSecondaryColor,
            onVegetarianPrimaryColorChange,
            onVegetarianSecondaryColorChange,
            ['Vegetarian'],
            { primary: '#32CD32', secondary: '#36AB36' }
          )}

          {renderTagSection(
            'Gluten-Free Tags',
            'gluten',
            glutenFreePrimaryColor,
            glutenFreeSecondaryColor,
            onGlutenFreePrimaryColorChange,
            onGlutenFreeSecondaryColorChange,
            ['Gluten Free', 'GF'],
            { primary: '#FFB347', secondary: '#FF8C00' }
          )}

          {renderTagSection(
            'Spicy Tags',
            'spicy',
            spicyPrimaryColor,
            spicySecondaryColor,
            onSpicyPrimaryColorChange,
            onSpicySecondaryColorChange,
            ['Spicy', 'Hot', '🌶️'],
            { primary: '#FF6B6B', secondary: '#FF3333' }
          )}

          {renderTagSection(
            'Dairy-Free Tags',
            'dairy',
            dairyFreePrimaryColor,
            dairyFreeSecondaryColor,
            onDairyFreePrimaryColorChange,
            onDairyFreeSecondaryColorChange,
            ['Dairy Free', 'DF'],
            { primary: '#87CEEB', secondary: '#4682B4' }
          )}

          {renderTagSection(
            'Nut-Free Tags',
            'nut',
            nutFreePrimaryColor,
            nutFreeSecondaryColor,
            onNutFreePrimaryColorChange,
            onNutFreeSecondaryColorChange,
            ['Nut Free', 'NF'],
            { primary: '#DEB887', secondary: '#8B7355' }
          )}

          {renderTagSection(
            'Halal Tags',
            'halal',
            halalPrimaryColor,
            halalSecondaryColor,
            onHalalPrimaryColorChange,
            onHalalSecondaryColorChange,
            ['Halal'],
            { primary: '#019000', secondary: '#B8860B' }
          )}

          {renderTagSection(
            'Popular Tags',
            'popular',
            popularPrimaryColor,
            popularSecondaryColor,
            onPopularPrimaryColorChange,
            onPopularSecondaryColorChange,
            ['Popular', 'Most Liked', 'Recommended', 'Must Try', '⭐'],
            { primary: '#b400fa', secondary: '#ff0000' }
          )}

          {renderTagSection(
            'New Tags',
            'new',
            newPrimaryColor,
            newSecondaryColor,
            onNewPrimaryColorChange,
            onNewSecondaryColorChange,
            ['New', 'Limited Time', 'Seasonal', '🆕'],
            { primary: '#ff0000', secondary: '#3f92ff' }
          )}

          {renderTagSection(
            'Deal Tags',
            'deal',
            dealPrimaryColor,
            dealSecondaryColor,
            onDealPrimaryColorChange,
            onDealSecondaryColorChange,
            ['Deal', 'Special', 'Combo', '💰'],
            { primary: '#4fc060', secondary: '#ff0000' }
          )}
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default TagBadgeStyleEditor;